import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiSun, FiMoon, FiGrid, FiPrinter } from 'react-icons/fi';
import { useTheme } from '../context/ThemeContext';
import { useLocale } from '../context/LocaleContext';
import { useAuth } from '../context/AuthContext';
import { useModule } from '../context/ModuleContext';
import * as customerService from '../services/customerService';
import { RECEIPT_TYPES, getReceiptType, typeHasCurrency, RECEIPT_CURRENCIES, CURRENCY_SYMBOLS, computeSuggestion } from '../config/receipts.config';
import ReceiptPreview from '../components/ReceiptPreview';
import '../receipt-print.css';

export default function ReceiptsDashboard() {
  const { user, logout } = useAuth();
  const { t, locale, setLocale } = useLocale();
  const { theme, toggleTheme } = useTheme();
  const { goToPicker, moduleParams, clearModuleParams } = useModule();

  const [typeId, setTypeId] = useState(RECEIPT_TYPES[0].id);
  const [values, setValues] = useState({});
  const [currency, setCurrency] = useState('USD');
  const [customers, setCustomers] = useState([]);

  const type = useMemo(() => getReceiptType(typeId), [typeId]);
  const showCurrency = useMemo(() => typeHasCurrency(type), [type]);

  // Load customers once for the name comboboxes (datalist suggestions).
  useEffect(() => {
    const load = async () => {
      const result = await customerService.listCustomers();
      if (result.success && result.data) setCustomers(result.data);
    };
    load();
  }, []);

  // Reset form values when the receipt type changes.
  useEffect(() => {
    setValues({});
    lastAutoFill.current = {};
  }, [typeId]);

  // Prefill from a shortcut (e.g. Custody's "Make receipt"): select the type and
  // fill the values. Applied after the type-reset effect above (setTimeout 0).
  useEffect(() => {
    if (moduleParams && moduleParams.receipt) {
      const { type: preType, values: preValues } = moduleParams.receipt;
      if (preType) setTypeId(preType);
      setTimeout(() => setValues(preValues || {}), 0);
      clearModuleParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleParams]);

  const setValue = (key, val) => setValues((prev) => ({ ...prev, [key]: val }));

  // Track the last auto-filled value per field so we only overwrite values the
  // user hasn't manually changed (auto-fill should not clobber manual edits).
  const lastAutoFill = useRef({});

  // Auto-fill fields whose `suggest.autoFill` is set, from their source fields.
  useEffect(() => {
    type.fields.forEach((f) => {
      if (!f.suggest || !f.suggest.autoFill) return;
      const sug = computeSuggestion(f, values);
      if (sug === null) return;
      const sugStr = sug.toFixed(2);
      const current = values[f.key];
      // Fill if empty, or if current still equals the previous auto-filled value.
      if (current === undefined || current === '' || current === lastAutoFill.current[f.key]) {
        if (current !== sugStr) {
          lastAutoFill.current[f.key] = sugStr;
          setValues((prev) => ({ ...prev, [f.key]: sugStr }));
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.peso, values.preco, typeId]);

  const missingRequired = type.fields
    .filter((f) => f.required)
    .filter((f) => {
      const v = values[f.key];
      return v === undefined || v === null || String(v).trim() === '';
    });

  const canPrint = missingRequired.length === 0;

  const handlePrint = () => {
    if (!canPrint) return;
    window.print();
  };

  const NUMERIC_TYPES = ['currency', 'fixedCurrency', 'weight', 'percent', 'number'];
  const inputModeFor = (fieldType) => (NUMERIC_TYPES.includes(fieldType) ? 'decimal' : 'text');
  const isNumeric = (fieldType) => NUMERIC_TYPES.includes(fieldType);

  return (
    <div className="layout">
      <header className="app-header">
        <div className="brand">
          <img src={process.env.PUBLIC_URL + '/assets/logo.png'} alt="Logo" />
          <h1 className="app-title">{t('brand_title')}</h1>
        </div>
        <div className="app-actions">
          <button className="btn btn-ghost btn-icon" onClick={goToPicker} title={t('back_to_modules')} aria-label={t('back_to_modules')}>
            <FiGrid />
          </button>
          <button
            className="btn btn-ghost btn-icon"
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <FiSun /> : <FiMoon />}
          </button>
          <select
            className="lang-select"
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            aria-label="Language"
            title="Language"
          >
            <option value="en">English</option>
            <option value="pt-BR">Português (BR)</option>
          </select>
          <span className="muted">{t('signed_in_as')} {user?.username}</span>
          <button className="btn btn-ghost" onClick={logout}>{t('logout')}</button>
        </div>
      </header>

      <main className="content">
        {/* Left: type + form */}
        <section className="panel details">
          <div className="details-header">
            <div>
              <h2>{t('receipts')}</h2>
              <div className="muted small">{t('receipts_desc')}</div>
            </div>
          </div>

          <div className="card">
            <label className="wide">
              <span>{t('receipt_type')}</span>
              <select
                className="currency-select"
                value={typeId}
                onChange={(e) => setTypeId(e.target.value)}
                style={{ width: '100%' }}
              >
                {RECEIPT_TYPES.map((rt) => (
                  <option key={rt.id} value={rt.id}>{t(rt.titleKey)}</option>
                ))}
              </select>
            </label>

            {showCurrency && (
              <label className="wide" style={{ marginTop: '12px' }}>
                <span>{t('currency')}</span>
                <select
                  className="currency-select"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  style={{ width: '100%' }}
                >
                  {RECEIPT_CURRENCIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </label>
            )}

            <form className="form-grid" onSubmit={(e) => { e.preventDefault(); handlePrint(); }} style={{ marginTop: '12px' }}>
              {type.fields.map((f) => (
                <label key={f.key} className="wide">
                  <span>
                    {t(f.labelKey)}
                    {f.type === 'fixedCurrency' ? ` (${f.fixedCurrency})` : ''}
                    {f.required ? ' *' : ''}
                    {f.optional ? ` (${t('optional')})` : ''}
                  </span>
                  <div className="input-group">
                    {(() => {
                      if (f.type === 'currencyPick') return null; // has its own currency select
                      const sym = f.type === 'currency'
                        ? CURRENCY_SYMBOLS[currency]
                        : f.type === 'fixedCurrency'
                          ? CURRENCY_SYMBOLS[f.fixedCurrency]
                          : f.prefix;
                      return sym ? <span className="prefix" aria-hidden>{sym}</span> : null;
                    })()}
                    {f.type === 'currencyPick' ? (
                      <>
                        <select
                          className="currency-select"
                          value={values[`${f.key}_cur`] || 'USD'}
                          onChange={(e) => setValue(`${f.key}_cur`, e.target.value)}
                          aria-label={t('currency')}
                        >
                          {RECEIPT_CURRENCIES.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                        <input
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          value={values[f.key] || ''}
                          onChange={(e) => setValue(f.key, e.target.value)}
                          placeholder="0.00"
                        />
                      </>
                    ) : f.suggest && f.suggest.autoFill ? (
                      // Auto-computed total: display only, not editable.
                      <span className="rcpt-readonly">
                        {(() => {
                          const sug = computeSuggestion(f, values);
                          return sug === null ? '—' : sug.toFixed(2);
                        })()}
                      </span>
                    ) : f.type === 'customer' ? (
                      <>
                        <input
                          type="text"
                          list={`customers-${f.key}`}
                          value={values[f.key] || ''}
                          onChange={(e) => setValue(f.key, e.target.value)}
                          placeholder={t(f.labelKey)}
                          required={f.required}
                        />
                        <datalist id={`customers-${f.key}`}>
                          {customers.map((c) => (
                            <option key={c.id} value={c.name} />
                          ))}
                        </datalist>
                      </>
                    ) : (
                      <input
                        type={isNumeric(f.type) ? 'number' : 'text'}
                        inputMode={inputModeFor(f.type)}
                        step={f.type === 'number' ? '1' : '0.01'}
                        value={values[f.key] || ''}
                        onChange={(e) => setValue(f.key, e.target.value)}
                        placeholder={t(f.labelKey)}
                        required={f.required}
                      />
                    )}
                    {f.suffix && <span className="prefix" aria-hidden>{f.suffix}</span>}
                  </div>
                </label>
              ))}

              <div className="actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!canPrint}
                  title={canPrint ? t('print_receipt') : t('fill_required_fields')}
                >
                  <FiPrinter style={{ marginRight: '6px' }} />
                  {t('print_receipt')}
                </button>
              </div>
            </form>
          </div>
        </section>

        {/* Right: live preview */}
        <section className="panel details">
          <div className="details-header">
            <div>
              <h2>{t('preview')}</h2>
              <div className="muted small">{t('preview_desc')}</div>
            </div>
          </div>
          <div style={{ padding: '20px 0', display: 'flex', justifyContent: 'center' }}>
            <ReceiptPreview type={type} values={values} currency={currency} t={t} />
          </div>
        </section>
      </main>
    </div>
  );
}
